import {
  Component,
  EmbeddedViewRef,
  HostListener,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import momentJalaali from 'moment-jalaali';
import axios from 'src/app/Service/axios';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import * as XLSX from 'xlsx';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { Subject, takeUntil } from 'rxjs';
import {
  ActivityRow,
  ApiStatus,
  ClassOption,
  DomainRow,
  EntityId,
  GradeOption,
  PermissionRow,
  UserClassRow,
  UserGroup,
  UserManagementPermissions,
  UserRow,
  UserSortKey,
  VehicleRow,
} from './saveUser.models';
import { compareUsers, filterFirstMatchingField } from './saveUser.utils';

declare let $: any;

@Component({
  selector: 'saveUser',
  templateUrl: './saveUser.component.html',
  styleUrls: ['./saveUser.component.css'],
})
export class SaveUserComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private destroyed = false;
  listLoading = false;
  hasError = false;
  searchText = '';
  currentPage = 1;
  pageSize = 20;
  sortKey: UserSortKey | '' = '';
  sortDirections: Record<UserSortKey, boolean> = {
    name: false,
    date: false,
    input: false,
    lastLogin: false,
  };

  private get requestContext() {
    return {
      iSystemUser: this.gurdService.decrypt(localStorage.getItem('ID')!),
      strSession: this.gurdService.decrypt(localStorage.getItem('Session')!),
    };
  }

  private isSessionExpired(result: ApiStatus, notify = true): boolean {
    if (!result?.ISSessionExpired && !result?.ISessionExpired) return false;
    if (notify) this.notifyService.showError('لطفا وارد سیستم شوید');
    this.otherService.removeLocalStorage();
    this.router.navigate(['login']);
    return true;
  }

  private hasApiError(result: ApiStatus | any, notifySession = true): boolean {
    if (result?.iResult == 0) {
      this.notifyService.showError(result.strError);
      return true;
    }
    return this.isSessionExpired(result, notifySession);
  }

  get pagedUsers(): UserRow[] {
    return this.visibleUsers.slice(
      (this.currentPage - 1) * this.pageSize,
      this.currentPage * this.pageSize,
    );
  }

  openUserModal(id: string): void {
    this.closeRowMenu(false);
    $('#' + id).modal('show');
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.closeRowMenu(false);
    this.destroy$.next();
    this.destroy$.complete();
    $('#addUser, #carModal').off('.saveUsers');
  }

  @ViewChild('rowMoreMenu') private rowMenuTemplate!: TemplateRef<unknown>;
  rowMenuRow: UserRow | null = null;
  private rowMenuView?: EmbeddedViewRef<unknown>;
  private rowMenuElement?: HTMLElement;
  private rowMenuTrigger?: HTMLElement;
  private readonly onMenuScroll = (event: Event) => {
    if (
      event.target instanceof Node &&
      this.rowMenuElement?.contains(event.target)
    )
      return;
    this.closeRowMenu(false);
  };

  toggleRowMenu(event: Event, row: UserRow): void {
    if (this.listLoading || this.destroyed) return;
    const alreadyOpen = this.rowMenuRow === row;
    this.closeRowMenu(false);
    if (alreadyOpen) return;
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;
    this.rowMenuTrigger = trigger;
    this.rowMenuRow = row;
    this.rowMenuView = this.views.createEmbeddedView(this.rowMenuTemplate);
    this.rowMenuView.detectChanges();
    const menu = this.rowMenuView.rootNodes.find(
      (node) => node instanceof HTMLElement,
    ) as HTMLElement | undefined;
    if (!menu) {
      this.closeRowMenu(false);
      return;
    }
    this.rowMenuElement = menu;
    document.body.appendChild(menu);
    const page = trigger.closest<HTMLElement>('.table-page');
    if (page) menu.style.fontFamily = getComputedStyle(page).fontFamily;
    const card = trigger.closest<HTMLElement>('.card')?.getBoundingClientRect();
    const leftEdge = Math.max(8, (card?.left ?? 0) + 8);
    const rightEdge = Math.min(innerWidth - 8, (card?.right ?? innerWidth) - 8);
    const availableWidth = Math.max(1, rightEdge - leftEdge);
    menu.style.minWidth = `${Math.min(150, availableWidth)}px`;
    menu.style.maxWidth = `${availableWidth}px`;
    const rect = trigger.getBoundingClientRect();
    const width = menu.offsetWidth;
    menu.style.maxHeight = `${Math.max(1, innerHeight - 16)}px`;
    menu.style.overflowY = 'auto';
    const height = menu.offsetHeight;
    menu.style.left = `${Math.max(leftEdge, Math.min(rect.right - width, rightEdge - width))}px`;
    menu.style.top = `${rect.bottom + height + 6 <= innerHeight ? rect.bottom + 6 : Math.max(8, rect.top - height - 6)}px`;
    document.addEventListener('scroll', this.onMenuScroll, true);
    menu.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  }

  closeRowMenu(restoreFocus = true): void {
    const trigger = this.rowMenuTrigger;
    document.removeEventListener('scroll', this.onMenuScroll, true);
    this.rowMenuView?.destroy();
    this.rowMenuElement?.remove();
    this.rowMenuView = undefined;
    this.rowMenuElement = undefined;
    this.rowMenuTrigger = undefined;
    this.rowMenuRow = null;
    if (restoreFocus && trigger?.isConnected) trigger.focus();
  }

  onRowMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      this.closeRowMenu();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = Array.from(
      this.rowMenuElement?.querySelectorAll<HTMLButtonElement>(
        'button:not(:disabled)',
      ) ?? [],
    );
    if (!buttons.length) return;
    const current = buttons.indexOf(
      document.activeElement as HTMLButtonElement,
    );
    const index =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? buttons.length - 1
          : (current + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) %
            buttons.length;
    buttons[index].focus();
  }

  @HostListener('document:click', ['$event'])
  onOutsideRowMenuClick(event: Event): void {
    if (
      this.rowMenuRow &&
      event.target instanceof Node &&
      !this.rowMenuElement?.contains(event.target) &&
      !this.rowMenuTrigger?.contains(event.target)
    )
      this.closeRowMenu(false);
  }

  @HostListener('document:keydown.escape')
  onRowMenuEscape(): void {
    this.closeRowMenu();
  }

  @HostListener('window:resize')
  onRowMenuResize(): void {
    this.closeRowMenu(false);
  }
  visibleUsers: UserRow[] | any[] = [];
  allUsers: UserRow[] = [];
  showUserDetails = false;
  editingUserId!: EntityId;
  isEditing = false;
  visibleVehicles: VehicleRow[] = [];
  allVehicles: VehicleRow[] = [];
  assignedVehicleCount: number = 0;
  selectedUserId!: EntityId;
  allPermissions: PermissionRow[] = [];
  visiblePermissions: PermissionRow[] = [];
  permissions!: UserManagementPermissions;
  visibleActivity: ActivityRow[] = [];
  allActivity: ActivityRow[] = [];
  assignedVehiclesOnly = false;
  isSchoolMode = false;
  visibleDomains: DomainRow[] = [];
  allDomains: DomainRow[] = [];
  userGroups: UserGroup[] = [];
  grades: GradeOption[] = [];
  classes: ClassOption[] = [];
  userClasses: UserClassRow[] = [];
  domainUserId: EntityId = '';
  parentUserId: EntityId = -1;

  smsForm: FormGroup = this.formBuilder.group({
    iUserSystemUser: new FormControl(),
    strSMS: new FormControl(''),
    ...this.requestContext,
  });

  userForm: FormGroup = this.formBuilder.group({
    iMaxInput: new FormControl(),
    strUserName: new FormControl(),
    iMaxMobile: new FormControl(1),
    strMobile: new FormControl(),
    strEsterakEndDate: new Date().valueOf(),
    strFullName: new FormControl(),
    strPassword: new FormControl(),
    bEnable: new FormControl('true'),
    ...this.requestContext,
  });

  userGroupForm: FormGroup = this.formBuilder.group({
    ...this.requestContext,
    iUserSystemUser: new FormControl(''),
    iSystemUser_Group: new FormControl(''),
  });

  userClassForm: FormGroup = this.formBuilder.group({
    ...this.requestContext,
    iUserSystemUser: new FormControl(''),
    iMobileDomain: new FormControl('-1'),
    tiGrade: new FormControl('-1'),
    tiClassNo: new FormControl('-1'),
  });

  constructor(
    private notifyService: NotificationService,
    private router: Router,
    public otherService: OtherService,
    private views: ViewContainerRef,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private gurdService: GurdServiceService,
  ) {}

  ngOnInit(): void {
    this.route.url.pipe(takeUntil(this.destroy$)).subscribe((segment) => {
      const router = segment[0].path;
      if (router == 'userSchool') this.isSchoolMode = true;
      else this.loadUserGroups();
      this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        if (params['iSystemUser'] != undefined) {
          this.parentUserId = params['iSystemUser'];
          this.loadUsers(params['iSystemUser']);
        } else this.reloadUsers();
      });
      let data = window.localStorage.getItem('menu') as any;
      this.permissions = JSON.parse(this.gurdService.decrypt(data));
    });
    $('#addUser').on('hidden.bs.modal.saveUsers', () => {
      this.showUserDetails = false;
      this.isEditing = false;
      this.userForm.reset({
        ...this.requestContext,
        strPassword: '',
        strFullName: '',
        iMaxInput: '',
        strMobile: '',
        bEnable: 'true',
        strEsterakEndDate: momentJalaali().format('jYYYY/jMM/jDD'),
      });
      this.setIdentityFieldsEnabled(true);
    });
    $('#carModal').on('hidden.bs.modal.saveUsers', () => {
      $('#filterModal').val('');
      this.visibleVehicles = [];
      this.allVehicles = [];
      this.assignedVehiclesOnly = false;
    });
  }
  loadUserGroups = async () => {
    try {
      const model = this.otherService.stringNumberToNumber({
        ...this.requestContext,
      });
      const resp = await axios.post('SystemUser_Group_List', model);
      if (!this.destroyed)
        this.userGroups =
          typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data;
    } catch {
      if (!this.destroyed)
        this.notifyService.showError('دریافت گروه‌های کاربری ناموفق بود');
    }
  };

  isNumber(event: KeyboardEvent): boolean {
    return !isNaN(Number(event.key));
  }
  reloadUsers = async () => this.loadUsers(this.parentUserId);

  private async loadUsers(targetUser: number | string): Promise<void> {
    if (this.listLoading || this.destroyed) return;
    this.closeRowMenu(false);
    this.listLoading = true;
    this.hasError = false;
    try {
      const model = this.otherService.stringNumberToNumber({
        iSystemUser: this.gurdService.decrypt(localStorage.getItem('ID')!),
        iLog_SystemUser:
          targetUser != -1
            ? targetUser
            : this.gurdService.decrypt(localStorage.getItem('ID')!),
        strSession: this.gurdService.decrypt(localStorage.getItem('Session')!),
      });
      const resp = await axios.post('GetRadyabUserUsers_New', model);
      if (this.destroyed) return;
      const rows =
        typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data;
      if (this.isSessionExpired(rows, false)) return;
      if (!Array.isArray(rows)) throw new Error('Invalid user list');
      this.allUsers = rows;
      this.searchUsers(this.searchText);
    } catch {
      if (!this.destroyed) this.hasError = true;
    } finally {
      this.listLoading = false;
    }
  }

  searchUsers(value: string): void {
    this.closeRowMenu(false);
    this.sortKey = '';
    this.searchText = value;
    const query = this.searchText.trim().toLocaleLowerCase();
    this.visibleUsers = this.allUsers.filter((item: any) =>
      ['strFullName', 'strMobile_strComment', 'strUserName'].some((key) =>
        String(item[key] ?? '')
          .toLocaleLowerCase()
          .includes(query),
      ),
    );
    this.currentPage = 1;
  }

  sortUsers(key: UserSortKey): void {
    this.sortKey = key;
    this.closeRowMenu(false);
    this.currentPage = 1;
    this.sortDirections[key] = !this.sortDirections[key];
    const direction = this.sortDirections[key] ? 1 : -1;
    this.visibleUsers.sort((a, b) => direction * compareUsers(a, b, key));
  }
  sendSms = async () => {
    let model = this.smsForm.value;
    model.iUserSystemUser = this.selectedUserId;
    const resp = await axios.post(`SendSMS2MobileUser`, model);
    resp.data = JSON.parse(resp.data);
    if (this.hasApiError(resp.data)) return;
    else {
      this.notifyService.showSuccess('با موفقیت ارسال شد');
      this.smsForm.controls['strSMS'].setValue('');
    }
  };

  searchVehicles(input: { value: string }): void {
    this.visibleVehicles = filterFirstMatchingField(
      this.allVehicles,
      [
        'strFullName',
        'strUnitID',
        'strSystemUser_strMobile',
        'strMobileDomain_strComment',
      ],
      input.value,
    );
  }

  searchVehicleDomains(val: { value: string }) {
    this.visibleVehicles = this.allVehicles.filter((vehicle) =>
      vehicle.strMobileDomain_strComment.includes(val.value),
    );
  }

  searchPermissions(val: { value: string }) {
    this.visiblePermissions = this.allPermissions.filter((permission) =>
      permission.strComment?.includes(val.value),
    );
  }

  private setIdentityFieldsEnabled(enabled: boolean): void {
    for (const key of ['strMobile', 'strUserName']) {
      if (enabled) this.userForm.controls[key].enable();
      else this.userForm.controls[key].disable();
    }
  }
  verifyUserIdentity = async () => {
    let model = this.userForm.value;
    const resp = await axios.post(`CheckRadyabUserUser`, model);
    resp.data = JSON.parse(resp.data);
    if (this.hasApiError(resp.data)) return;
    else {
      this.notifyService.showSuccess(resp.data.strError);
      this.showUserDetails = true;
      this.setIdentityFieldsEnabled(false);
    }
  };
  addUser = async () => {
    this.setIdentityFieldsEnabled(true);
    let model = this.userForm.value;
    const resp = await axios.post(`AddRadyabUser`, model);
    resp.data = JSON.parse(resp.data);
    if (!model.strFullName) {
      this.notifyService.showError('لطفا نام نام خانوادگی را وارد کنید');
      return;
    }
    if (!model.strPassword) {
      this.notifyService.showError('لطفا رمز عبور را وارد کنید');
      return;
    }
    if (!model.iMaxInput) {
      this.notifyService.showError('لطفا حداکثر تعداد ورود وارد کنید');
      return;
    }
    if (!model.iMaxMobile) {
      this.notifyService.showError('لطفا حداکثر تعداد خودرو وارد کنید');
      return;
    }
    if (this.hasApiError(resp.data)) return;
    else {
      this.notifyService.showSuccess(resp.data.strError);
      $('#addUser').modal('hide');
      this.reloadUsers();
    }
  };
  deleteUser = async (id: EntityId) => {
    $.confirm({
      title: `آیا از حذف این کاربر اطمینان دارید؟`,
      content: '',
      type: 'red',
      buttons: {
        ok: {
          text: 'تایید',
          btnClass: 'btn-primary',
          keys: ['enter'],
          action: async () => {
            let model = {
              ...this.requestContext,
              iSystemUser4Delete: id,
            };
            const resp = await axios.post(`DeleteRadyabUser`, model);
            resp.data = JSON.parse(resp.data);
            if (this.hasApiError(resp.data)) return;
            else {
              this.notifyService.showSuccess('با موفقیت حذف شد');
              this.allUsers = this.allUsers.filter(
                (item) => item.iSystemUser != id,
              );
              this.searchUsers(this.searchText);
            }
          },
        },
        cancel: {
          text: 'لغو',
        },
      },
    });
  };

  prepareUserEdit(item: UserRow): void {
    this.setIdentityFieldsEnabled(false);
    this.showUserDetails = true;
    this.userForm.patchValue({
      iMaxInput: item.iMaxInput,
      strMobile: item.strMobile,
      strEsterakEndDate: item.strEsterakEndDate,
      strFullName: item.strFullName,
      strPassword: item.strSipPassword,
      bEnable: String(item.bEnable),
      iMaxMobile: item.iMaxMobile,
      strUserName: item.strUserName,
    });
    this.editingUserId = item.iSystemUser;
    this.isEditing = true;
  }
  updateUser = async () => {
    let model = this.userForm.value;
    model.iUserSystemUser = this.editingUserId;
    if (!model.strFullName) {
      this.notifyService.showError('لطفا نام نام خانوادگی را وارد کنید');
      return;
    }
    if (!model.strPassword) {
      model.strPassword = '';
    }
    if (!model.iMaxInput) {
      this.notifyService.showError('لطفا حداکثر تعداد ورود را وارد کنید');
      return;
    }
    if (!model.iMaxMobile) {
      this.notifyService.showError('لطفا حداکثر تعداد خودود را وارد کنید');
      return;
    }
    const resp = await axios.post(`EditRadyabUser`, model);
    resp.data = JSON.parse(resp.data);
    if (this.hasApiError(resp.data)) return;
    else {
      this.notifyService.showSuccess(resp.data.strError);
      $('#addUser').modal('hide');
      this.reloadUsers();
    }
  };
  loadUserVehicles = async (id: EntityId) => {
    let model = {
      ...this.requestContext,
      iUserSystemUser: id,
    };
    this.selectedUserId = id;
    this.otherService.Showspinner();
    const resp = await axios.post(`GetRadyabUserMobile`, model);
    this.otherService.nonespinner();
    if (this.hasApiError(resp.data)) return;
    else {
      this.visibleVehicles = JSON.parse(resp.data);
      this.assignedVehicleCount = this.visibleVehicles.reduce(
        (total, vehicle) => total + vehicle.bHas,
        0,
      );
      this.allVehicles = this.visibleVehicles.map((vehicle) => ({
        ...vehicle,
      }));
    }
    this.visibleVehicles.sort((a, b) => b.bHas - a.bHas);
  };
  toggleVehicleAssignment = async (id: EntityId) => {
    let model = {
      ...this.requestContext,
      iUserSystemUser: this.selectedUserId,
      strUnitID: id,
    };
    const resp = await axios.post(`AddOrRemoveMobileFromUser`, model);
    if (this.hasApiError(resp.data)) return;
  };
  loadUserPermissions = async (id: EntityId) => {
    this.selectedUserId = id;
    let model = this.otherService.stringNumberToNumber({
      ...this.requestContext,
      iUserSystemUser: id,
    });
    this.otherService.Showspinner();
    const resp = await axios.post(`GetRadyabUserAbility`, model);
    this.otherService.nonespinner();
    if (this.isSessionExpired(resp.data, false)) return;
    this.allPermissions = JSON.parse(resp.data);
    this.visiblePermissions = this.allPermissions.sort(
      (a, b) => b.bHas - a.bHas,
    );
  };
  togglePermission = async (id: EntityId) => {
    let model = {
      ...this.requestContext,
      iUserSystemUser: this.selectedUserId,
      strXenotakAbility: id,
    };
    const resp = await axios.post(`AddOrRemoveUserAbility`, model);
    if (this.hasApiError(resp.data)) return;
  };

  toggleAssignedVehicles() {
    this.assignedVehiclesOnly = !this.assignedVehiclesOnly;
    this.visibleVehicles = this.assignedVehiclesOnly
      ? this.allVehicles.filter((vehicle) => vehicle.bHas != 0)
      : this.allVehicles;
  }

  async setVehicleAssignments(assign: boolean): Promise<void> {
    const model = {
      ...this.requestContext,
      iUserSystemUser: this.selectedUserId,
      lstUnitID: this.visibleVehicles
        .filter((vehicle) => Boolean(vehicle.bHas) !== assign)
        .map((vehicle) => vehicle.strUnitID),
    };
    const response = await axios.post('AddGroupMobilesFromUser', model);
    if (this.hasApiError(response.data)) return;
    this.visibleVehicles.forEach((vehicle) => (vehicle.bHas = assign ? 1 : 0));
  }

  async setPermissionAssignments(assign: boolean): Promise<void> {
    const model = {
      ...this.requestContext,
      iUserSystemUser: this.selectedUserId,
      bAddOrRemove: assign ? 1 : 0,
      lststrXenotakAbility: this.visiblePermissions.map(
        (permission) => permission.strXenotakAbility,
      ),
    };
    this.otherService.Showspinner();
    try {
      const response = await axios.post('AddOrRemoveAllUserAbility', model);
      const result =
        typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;
      if (this.hasApiError(result)) return;
      this.allPermissions.forEach(
        (permission) => (permission.bHas = assign ? 1 : 0),
      );
      $('#access').modal('hide');
    } finally {
      this.otherService.nonespinner();
    }
  }
  loadUserActivity = async (id: EntityId) => {
    this.visibleActivity = [];
    const model = { ...this.requestContext, iLog_SystemUser: id };
    const resp = await axios.post(`GetRadyabUserLog`, model);
    if (this.isSessionExpired(resp.data, false)) return;
    this.visibleActivity = JSON.parse(resp.data);
    this.allActivity = this.visibleActivity.slice();
  };

  searchActivity(event: Event): void {
    this.visibleActivity = filterFirstMatchingField(
      this.allActivity,
      ['strActionType_strComment', 'strDate'],
      (event.target as HTMLInputElement).value,
    );
  }

  openChildUsers(iSystemUser: EntityId) {
    window.open(`saveUser/${iSystemUser}`, '_blank');
  }

  exportExcel(id: string): void {
    if (id === 'excel-table-area-main') {
      const rows = this.visibleUsers.map((item) => ({
        'نام و نام خانوادگی': item.strFullName,
        'نام کاربری': item.strUserName,
        ...(!this.isSchoolMode
          ? {
              'حوزه خدمتی': item.strMobile_strComment,
              'تاریخ پایان اعتبار': item.strEsterakEndDate,
            }
          : {}),
        'تعداد ورود': item.iInput,
        ...(!this.isSchoolMode ? { 'حداکثر تعداد ورود': item.iMaxInput } : {}),
        'تاریخ آخرین ورود': item.strLastLoginDateTime,
        ...(!this.isSchoolMode ? { 'تعداد خودرو مجاز': item.iMaxMobile } : {}),
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(rows),
        'Users',
      );
      XLSX.writeFile(workbook, 'مدیریت کاربران.xlsx');
      return;
    }
    let element = document.getElementById(`${id}`);
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, 'ExcelSheet.xlsx');
  }

  async loadUserDomains(item: UserRow, isSupervision?: 'supervision_service') {
    this.visibleDomains = [];
    this.domainUserId = item.iSystemUser;
    let model = {
      iSystemUser_School: item.iSystemUser,
      ...this.requestContext,
    };
    let url = !this.isSchoolMode
      ? 'MobileDomain_ListUserAccess'
      : 'School_MobileDomain_List';
    url = isSupervision ? 'SystemUser_MobileDomain_List' : url;
    let res = JSON.parse((await axios.post(url, model)).data.toString());
    if (this.isSessionExpired(res, false)) return;
    this.visibleDomains = res;
    this.allDomains = res;
  }

  async setDomainAssignment(
    selected: boolean,
    item: DomainRow,
    supervision?: 'supervision_service',
  ): Promise<void> {
    const model = {
      ...this.requestContext,
      iMobileDomain: item.iMobileDomain,
      iSystemUser_School: this.domainUserId,
      bDelete: !selected,
    };
    const endpoint = supervision
      ? 'SystemUser_MobileDomain_SaveDelete'
      : 'School_User_SaveDelete';
    const response = await axios.post(endpoint, model);
    const result =
      typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data;
    if (this.hasApiError(result)) return;
    this.notifyService.showSuccess('عملیات با موفقیت انجام شد');
  }

  async selectDomain(item: DomainRow) {
    let model = {
      ...this.requestContext,
      iMobileDomain: item.iMobileDomain,
      iUserSystemUser: this.domainUserId,
    };
    let res: any = JSON.parse(
      (await axios.post(`SystemUser_UpdateMobieDomain`, model)).data.toString(),
    );
    if (res.iResult == 0) return this.notifyService.showError(res.strError);
    $('#defineSchool').modal('hide');
    this.visibleUsers.find(
      (user) => user.iSystemUser == this.domainUserId,
    ).strMobile_strComment = item.strComment;
    return this.notifyService.showSuccess('عملیات با موفقیت انجام شد');
  }

  openUserGroup(item: UserRow): void {
    this.userGroupForm.patchValue({
      iUserSystemUser: item.iSystemUser,
      iSystemUser_Group: '',
    });
    $('#Access_Modal').modal('show');
  }

  async assignUserGroup(item: UserGroup) {
    this.userGroupForm.patchValue({
      iSystemUser_Group: item.iSystemUser_Group,
    });
    this.otherService.Showspinner();
    let res = JSON.parse(
      (
        await axios.post(
          `SystemUser_SetSystemUser_Group`,
          this.otherService.stringNumberToNumber(this.userGroupForm.value),
        )
      ).data.toString(),
    );
    this.otherService.nonespinner();
    if (res.iResult == 0) return this.notifyService.showSuccess(res.strError);
    this.notifyService.showSuccess('عملیات با موفقیت انجام شد');
    $('#Access_Modal').modal('hide');
  }

  searchDomains(search: string) {
    if (search.length > 2) {
      this.visibleDomains = this.allDomains.filter(
        (domain) =>
          domain.strComment?.includes(search) ||
          domain.strCityName?.includes(search),
      );
    } else if (search == '') this.visibleDomains = this.allDomains;
  }

  async loadUserClasses(item: UserRow): Promise<void> {
    this.otherService.Showspinner();
    try {
      this.userClassForm.patchValue({ iUserSystemUser: item.iSystemUser });
      await this.loadUserDomains(item, 'supervision_service');
      if (!this.grades.length)
        this.grades =
          (await this.loadClassData<GradeOption>('Grade_List', item)) ?? [];
      if (!this.classes.length)
        this.classes =
          (await this.loadClassData<ClassOption>('ClassNo_List', item)) ?? [];
      const rows = await this.loadClassData<UserClassRow>(
        'School_SystemUserGradeClass_List',
        item,
      );
      if (!rows) return;
      this.userClasses = rows;
      $('#GradeClass_Modal').modal('show');
    } finally {
      this.otherService.nonespinner();
    }
  }
  
  private async loadClassData<T>(
    endpoint: string,
    user: UserRow,
  ): Promise<T[] | null> {
    this.userGroupForm.patchValue({ iUserSystemUser: user.iSystemUser });
    const response = await axios.post(
      endpoint,
      this.otherService.stringNumberToNumber(this.userGroupForm.value),
    );
    const result =
      typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data;
    if (this.hasApiError(result, false)) return null;
    return result;
  }

  openAddUserClass() {
    this.userClassForm.patchValue({
      iMobileDomain: '-1',
      tiGrade: '-1',
      tiClassNo: '-1',
    });
    $('#UserClassModal').modal('show');
  }

  async saveUserClass() {
    let model: any = this.userClassForm.value;
    if (
      model.iMobileDomain == -1 ||
      model.tiGrade == -1 ||
      model.tiClassNo == -1
    ) {
      return this.notifyService.showError('فرم را با دقت تکمیل کنید!');
    }
    this.otherService.Showspinner();
    let res = JSON.parse(
      (
        await axios.post(
          `School_SystemUserGradeClass_Save`,
          this.otherService.stringNumberToNumber(model),
        )
      ).data.toString(),
    );
    this.otherService.nonespinner();
    if (res.iResult == 0) return this.notifyService.showSuccess(res.strError);
    if (this.isSessionExpired(res, false)) return;
    this.userClasses.push(res[0]);
    this.notifyService.showSuccess('با موفقیت ثبت شد.');
    $('#UserClassModal').modal('hide');
  }

  deleteUserClass = async (id: EntityId, index: number) => {
    $.confirm({
      title: `آیا از حذف اطمینان دارید؟`,
      content: '',
      type: 'red',
      buttons: {
        ok: {
          text: 'تایید',
          btnClass: 'btn-danger',
          keys: ['enter'],
          action: async () => {
            let model = {
              ...this.requestContext,
              iSchool_SystemUserGradeClass: id,
            };
            const resp = await axios.post(
              `School_SystemUserGradeClass_Delete`,
              model,
            );
            resp.data = JSON.parse(resp.data);
            if (this.hasApiError(resp.data)) return;
            else {
              this.notifyService.showSuccess('با موفقیت حذف شد');
              this.userClasses.splice(index, 1);
            }
          },
        },
        cancel: {
          text: 'لغو',
        },
      },
    });
  };
}
